import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, Clock, BookOpen, Star, ChevronDown, ChevronUp, X, CheckCircle2 } from 'lucide-react';
// --- Types & Data ---
type Course = {
  id: number;
  title: string;
  category: 'Fitness' | 'Nutrition' | 'Business Skills' | 'Life Organisation';
  level: 'Beginner' | 'Intermediate' | 'All Levels';
  duration: string;
  price: number;
  instructor: {
    name: string;
    avatar: string;
    bio: string;
  };
  image: string;
  badge?: string;
  learningOutcomes: string[];
  modules: {
    title: string;
    lessons: string[];
  }[];
  faqs: {
    q: string;
    a: string;
  }[];
};
const COURSES: Course[] = [{
  id: 1,
  title: 'Strength Foundations',
  category: 'Fitness',
  level: 'Beginner',
  duration: '6 weeks',
  price: 699,
  instructor: {
    name: 'Thandi Nkosi',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1bf98c?auto=format&fit=crop&q=80&w=200',
    bio: 'Certified fitness coach with 10 years of experience transforming busy lives through sustainable movement.'
  },
  image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Master fundamental movement patterns safely', 'Build core strength and stability', 'Develop a consistent workout routine', 'Understand progressive overload'],
  modules: [{
    title: 'Module 1: The Basics',
    lessons: ['Form & Posture', 'Breathing Techniques', 'Core Activation']
  }, {
    title: 'Module 2: Lower Body',
    lessons: ['Squat Mechanics', 'Hinge Movements', 'Lunge Variations']
  }, {
    title: 'Module 3: Upper Body',
    lessons: ['Push Movements', 'Pull Movements', 'Shoulder Health']
  }],
  faqs: [{
    q: 'Do I need a gym membership?',
    a: 'No, this course includes both home and gym variations.'
  }, {
    q: 'How much time per week?',
    a: 'Plan for 3 sessions of 45 minutes each.'
  }]
}, {
  id: 2,
  title: 'HIIT & Burn',
  category: 'Fitness',
  level: 'Intermediate',
  duration: '4 weeks',
  price: 499,
  instructor: {
    name: 'Thandi Nkosi',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1bf98c?auto=format&fit=crop&q=80&w=200',
    bio: 'Certified fitness coach with 10 years of experience transforming busy lives through sustainable movement.'
  },
  image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Increase cardiovascular endurance', 'Maximize calorie burn in minimum time', 'Improve agility'],
  modules: [{
    title: 'Week 1: Conditioning',
    lessons: ['Interval Basics', 'Active Recovery']
  }, {
    title: 'Week 2: Power',
    lessons: ['Plyometrics', 'Speed Drills']
  }],
  faqs: [{
    q: 'Is this high impact?',
    a: 'Yes, but low-impact modifications are provided for every exercise.'
  }]
}, {
  id: 3,
  title: 'Meal Prep Mastery',
  category: 'Nutrition',
  level: 'Beginner',
  duration: '3 weeks',
  price: 399,
  instructor: {
    name: 'Chef Amahle',
    avatar: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?auto=format&fit=crop&q=80&w=200',
    bio: 'Culinary nutritionist specializing in time-saving, nutrient-dense meals for professionals.'
  },
  image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Plan a week of meals in 20 minutes', 'Batch cook efficiently', 'Store food safely to maximize freshness'],
  modules: [{
    title: 'Planning',
    lessons: ['The Formula', 'Grocery Hacks']
  }, {
    title: 'Execution',
    lessons: ['Batch Cooking', 'Flavor Profiles']
  }],
  faqs: [{
    q: 'Are recipes included?',
    a: 'Yes, over 30 adaptable recipes are included.'
  }]
}, {
  id: 4,
  title: 'Intuitive Eating Workshop',
  category: 'Nutrition',
  level: 'All Levels',
  duration: '2 weeks',
  price: 299,
  instructor: {
    name: 'Dr. Zanele',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200',
    bio: 'Registered Dietitian helping women break free from diet culture.'
  },
  image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Recognize hunger cues', 'Ditch diet mentality', 'Make peace with food'],
  modules: [{
    title: 'Core Principles',
    lessons: ['Rejecting Diet Culture', 'Honoring Hunger']
  }],
  faqs: [{
    q: 'Is this a weight loss program?',
    a: 'No, this focuses on healing your relationship with food.'
  }]
}, {
  id: 5,
  title: 'Small Business Compliance 101',
  category: 'Business Skills',
  level: 'Beginner',
  duration: '4 weeks',
  price: 599,
  instructor: {
    name: 'Amara Osei',
    avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=200',
    bio: 'Operations Director and compliance expert for SMEs.'
  },
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Understand CIPC requirements', 'Set up basic tax structures', 'Maintain clean records'],
  modules: [{
    title: 'Registration',
    lessons: ['Entity Types', 'CIPC Process']
  }],
  faqs: [{
    q: 'Does this cover tax filing?',
    a: 'It covers the basics of what you need to prepare for your accountant.'
  }]
}, {
  id: 6,
  title: 'Executive Presence',
  category: 'Business Skills',
  level: 'Intermediate',
  duration: '6 weeks',
  price: 899,
  instructor: {
    name: 'Naledi Mokoena',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=200',
    bio: 'Founder & CEO of Silvermark, former corporate strategist.'
  },
  image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Command a room', 'Communicate with authority', 'Manage stakeholder relationships'],
  modules: [{
    title: 'Communication',
    lessons: ['Vocal Tone', 'Body Language']
  }],
  faqs: [{
    q: 'Is this for managers only?',
    a: 'It is ideal for anyone looking to step into leadership.'
  }]
}, {
  id: 7,
  title: 'The Clarity Method',
  category: 'Life Organisation',
  level: 'All Levels',
  duration: '4 weeks',
  price: 499,
  badge: 'POPULAR',
  instructor: {
    name: 'Lerato Dlamini',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200',
    bio: 'Community Lead and productivity specialist.'
  },
  image: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Audit your time', 'Build a sustainable weekly routine', 'Automate admin tasks'],
  modules: [{
    title: 'Time Auditing',
    lessons: ['Tracking', 'Identifying Leaks']
  }],
  faqs: [{
    q: 'What tools do I need?',
    a: 'Just a notebook or basic digital calendar.'
  }]
}, {
  id: 8,
  title: 'Digital Declutter',
  category: 'Life Organisation',
  level: 'Beginner',
  duration: '2 weeks',
  price: 249,
  instructor: {
    name: 'Lerato Dlamini',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200',
    bio: 'Community Lead and productivity specialist.'
  },
  image: 'https://images.unsplash.com/photo-1512314889357-e157c22f938d?auto=format&fit=crop&q=80&w=800',
  learningOutcomes: ['Achieve Inbox Zero', 'Organize cloud storage', 'Reduce screen time'],
  modules: [{
    title: 'Inbox Management',
    lessons: ['Filters', 'Unsubscribing']
  }],
  faqs: [{
    q: 'Does this cover Mac or PC?',
    a: 'The principles apply to both.'
  }]
}];
const CATEGORIES = ['All', 'Fitness', 'Nutrition', 'Business Skills', 'Life Organisation'];
// --- Animations ---
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function Courses() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedCourse) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedCourse]);
  const filteredCourses = activeCategory === 'All' ? COURSES : COURSES.filter(c => c.category === activeCategory);
  return <main className="flex flex-col min-h-screen bg-warmwhite pt-20" data-id="element-386" data-mp="tt25ljvyqqa5">
      {/* HERO SECTION */}
      <section className="bg-plum py-24 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden" data-id="element-387" data-mp="3brhehizxbhcs">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-388" data-mp="3gjrb1hvjnfo1" />
        <motion.div initial="hidden" animate="visible" variants={fadeUpVariants} className="max-w-3xl mx-auto relative z-10" data-id="element-389" data-mp="36swmg6uts3zf">
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-390" data-mp="3ugwf5cbp64gn">
            Learn. Grow. Transform.
          </h1>
          <p className="text-warmwhite/80 text-lg md:text-xl font-body leading-relaxed" data-id="element-391" data-mp="3klck0s4yj9sy">
            Expert-led courses designed for the modern woman who never stops
            evolving. Reclaim your time and master your life.
          </p>
        </motion.div>
      </section>

      {/* PROGRESS TRACKER (Placeholder) */}
      <section className="py-12 bg-white border-b border-charcoal/5" data-id="element-392" data-mp="2e5a946vspkzd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-393" data-mp="w6ciik2f6lhu">
          <div className="flex items-center justify-between mb-6" data-id="element-394" data-mp="w8p211ntt3x0">
            <h2 className="font-display text-2xl font-bold text-plum" data-id="element-395" data-mp="r79e91u9kjax">
              Your Learning
            </h2>
            <span className="text-sm text-charcoal/50" data-id="element-396" data-mp="155g53y0spj13">Demo View</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-397" data-mp="116gvi044cy3b">
            <div className="bg-warmwhite p-6 rounded-2xl border border-charcoal/10 flex items-center gap-6" data-id="element-398" data-mp="dfl77cyvgl0t">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0" data-id="element-399" data-mp="8icnh7y2s8xg">
                <img src={COURSES[0].image} alt="" className="w-full h-full object-cover opacity-80" data-id="element-400" data-mp="m8y56tbsmctr" />
              </div>
              <div className="flex-1" data-id="element-401" data-mp="3px9isekjjdn7">
                <h4 className="font-bold text-charcoal mb-2" data-id="element-402" data-mp="3pw89ofn35184">
                  Strength Foundations
                </h4>
                <div className="w-full bg-charcoal/10 rounded-full h-2 mb-2" data-id="element-403" data-mp="3lud9jegw4tvx">
                  <div className="bg-fuchsia h-2 rounded-full" style={{
                  width: '45%'
                }} data-id="element-404" data-mp="1ova1xe4ykak3"></div>
                </div>
                <p className="text-xs text-charcoal/60" data-id="element-405" data-mp="1yng1pa1gbw9u">
                  45% Complete • Next: Lower Body Mechanics
                </p>
              </div>
              <button className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-plum hover:bg-fuchsia hover:text-white transition-colors" data-id="element-406" data-mp="15xr12xsnly8r">
                <PlayCircle className="w-5 h-5" data-id="element-407" data-mp="1fv58y90ihepg" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* COURSES CATALOGUE */}
      <section className="py-16 md:py-24" data-id="element-408" data-mp="1krvmaokxlw76">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-409" data-mp="2rattktqam1n3">
          {/* Tabs */}
          <div className="flex overflow-x-auto pb-4 mb-12 hide-scrollbar border-b border-charcoal/10" data-id="element-410" data-mp="2w8lwsjwe1a6l">
            <div className="flex gap-8 min-w-max px-2" data-id="element-411" data-mp="km9whh8iign4">
              {CATEGORIES.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`relative pb-4 text-lg font-display font-medium transition-colors ${activeCategory === category ? 'text-fuchsia' : 'text-charcoal/60 hover:text-charcoal'}`} data-id="element-412" data-mp="fmf75i4dxz46">
                  {category}
                  {activeCategory === category && <motion.div layoutId="course-tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-fuchsia rounded-t-full" data-id="element-413" data-mp="3t1brje2ynarm" />}
                </button>)}
            </div>
          </div>

          {/* Grid */}
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" key={activeCategory} // Force re-animation on tab change
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-414" data-mp="1maluborcr3ll">
            {filteredCourses.map(course => <motion.div key={course.id} variants={fadeUpVariants} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-charcoal/5 flex flex-col group cursor-pointer" onClick={() => setSelectedCourse(course)} data-id="element-415" data-mp="50fczir1rnzl">
                {/* Image */}
                <div className="relative aspect-video overflow-hidden" data-id="element-416" data-mp="36u1fy2rfxa">
                  <img src={course.image} alt={course.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-417" data-mp="3igwk4i5p3eac" />
                  {course.badge && <div className="absolute top-3 left-3 px-3 py-1 bg-gold text-charcoal text-xs font-bold tracking-wider rounded-full shadow-sm" data-id="element-418" data-mp="3df756rfcqggz">
                      {course.badge}
                    </div>}
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1" data-id="element-419" data-mp="33t5zfj0pqy8i">
                  <span className="inline-block px-3 py-1 bg-plum/10 text-plum text-xs font-bold rounded-full w-max mb-4" data-id="element-420" data-mp="33qjttoxdussx">
                    {course.category}
                  </span>

                  <h3 className="font-display font-bold text-xl text-charcoal mb-3 line-clamp-2" data-id="element-421" data-mp="3crhzebngz8dm">
                    {course.title}
                  </h3>

                  <div className="flex items-center gap-4 text-sm text-charcoal/70 mb-6" data-id="element-422" data-mp="3mjdxhmpk2yhu">
                    <div className="flex items-center gap-1" data-id="element-423" data-mp="3hjj7dldlucxu">
                      <Star className="w-4 h-4 text-gold fill-gold" data-id="element-424" data-mp="3hmdph695blo3" />
                      <span data-id="element-425" data-mp="cfmslamlmxu">{course.level}</span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-426" data-mp="3rj0bhyd7kirp">
                      <Clock className="w-4 h-4" data-id="element-427" data-mp="2g16n0qj1wu0" />
                      <span data-id="element-428" data-mp="2hc98x88kyjj">{course.duration}</span>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-charcoal/10 flex items-center justify-between" data-id="element-429" data-mp="7fvz38pyauh6">
                    <div className="flex items-center gap-2" data-id="element-430" data-mp="ccmf5w1r9q2l">
                      <img src={course.instructor.avatar} alt={course.instructor.name} className="w-8 h-8 rounded-full object-cover" data-id="element-431" data-mp="h573wj5t1cz9" />
                      <span className="text-sm font-medium text-charcoal" data-id="element-432" data-mp="q40vfv4a3d5y">
                        {course.instructor.name}
                      </span>
                    </div>
                    <span className="font-bold text-fuchsia text-lg" data-id="element-433" data-mp="102qfnujnd9pd">
                      R{course.price}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* COURSE DETAIL MODAL */}
      <AnimatePresence data-id="element-434" data-mp="2ucotry0wqyxa">
        {selectedCourse && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedCourse(null)} className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" data-id="element-435" data-mp="2pjvon91j1oko" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-x-4 top-10 bottom-10 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-4xl bg-white rounded-3xl shadow-2xl z-50 overflow-hidden flex flex-col" data-id="element-436" data-mp="38cpt391ixyan">
              {/* Close Button */}
              <button onClick={() => setSelectedCourse(null)} className="absolute top-4 right-4 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-md text-white rounded-full flex items-center justify-center transition-colors z-10" data-id="element-437" data-mp="27ml2hux79hvi">
                <X className="w-6 h-6" data-id="element-438" data-mp="2mkn43zko7ta4" />
              </button>

              <div className="overflow-y-auto flex-1 pb-24" data-id="element-439" data-mp="2mk536pn2cqvv">
                {/* Modal Hero */}
                <div className="relative h-64 md:h-80" data-id="element-440" data-mp="2qk5s547sgkh6">
                  <img src={selectedCourse.image} alt={selectedCourse.title} className="w-full h-full object-cover" data-id="element-441" data-mp="3ebb3b62saol5" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" data-id="element-442" data-mp="17miwgak6gee7" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white" data-id="element-443" data-mp="12n679j5u2v8g">
                    <span className="inline-block px-3 py-1 bg-fuchsia text-white text-xs font-bold rounded-full mb-4" data-id="element-444" data-mp="1hc2exf3za68j">
                      {selectedCourse.category}
                    </span>
                    <h2 className="font-display text-3xl md:text-5xl font-bold mb-4" data-id="element-445" data-mp="onn5r97j2i54">
                      {selectedCourse.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-6 text-sm md:text-base text-white/80" data-id="element-446" data-mp="jrpzjvpzas62">
                      <div className="flex items-center gap-2" data-id="element-447" data-mp="tnj8z68ei5jm">
                        <Star className="w-5 h-5 text-gold fill-gold" data-id="element-448" data-mp="tm841337yrqx" />{' '}
                        {selectedCourse.level}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-449" data-mp="2537tzs23guhs">
                        <Clock className="w-5 h-5" data-id="element-450" data-mp="254ssttvfyzlg" /> {selectedCourse.duration}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-451" data-mp="33sr4q6s7aqdd">
                        <BookOpen className="w-5 h-5" data-id="element-452" data-mp="2yte84ubj24xl" />{' '}
                        {selectedCourse.modules.length} Modules
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-453" data-mp="37um77elvb9sq">
                  {/* Main Content */}
                  <div className="md:col-span-2 space-y-12" data-id="element-454" data-mp="3hqxjw5pde1np">
                    {/* What You'll Learn */}
                    <section data-id="element-455" data-mp="3vhu7tj8cpuof">
                      <h3 className="font-display text-2xl font-bold text-plum mb-6" data-id="element-456" data-mp="3vgsmmlxmfrsz">
                        What You'll Learn
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-457" data-mp="9avra16qpowi">
                        {selectedCourse.learningOutcomes.map((outcome, i) => <div key={i} className="flex items-start gap-3" data-id="element-458" data-mp="4dc94q6x8rza" data-mpi={i}>
                            <CheckCircle2 className="w-5 h-5 text-fuchsia flex-shrink-0 mt-0.5" data-id="element-459" data-mp="nymd53ngkazx" />
                            <span className="text-charcoal/80" data-id="element-460" data-mp="svmj7ekb5dr1">{outcome}</span>
                          </div>)}
                      </div>
                    </section>

                    {/* Curriculum */}
                    <section data-id="element-461" data-mp="iwn8zrjw9a1x">
                      <h3 className="font-display text-2xl font-bold text-plum mb-6" data-id="element-462" data-mp="5736mmu86et5">
                        Curriculum
                      </h3>
                      <div className="space-y-4" data-id="element-463" data-mp="9yl7law90fe4">
                        {selectedCourse.modules.map((mod, i) => {
                      const isOpen = openAccordion === `mod-${i}`;
                      return <div key={i} className="border border-charcoal/10 rounded-xl overflow-hidden" data-id="element-464" data-mp="5w2cxrajxr8" data-mpi={i}>
                              <button onClick={() => setOpenAccordion(isOpen ? null : `mod-${i}`)} className="w-full px-6 py-4 flex items-center justify-between bg-warmwhite/50 hover:bg-warmwhite transition-colors text-left" data-id="element-465" data-mp="3mhb21356cyss">
                                <span className="font-bold text-charcoal" data-id="element-466" data-mp="1e9en6x2inbnu">
                                  {mod.title}
                                </span>
                                {isOpen ? <ChevronUp className="w-5 h-5 text-charcoal/50" data-id="element-467" data-mp="26u64s9d30nf6" /> : <ChevronDown className="w-5 h-5 text-charcoal/50" data-id="element-468" data-mp="1xvkrasmgvyl9" />}
                              </button>
                              <AnimatePresence data-id="element-469" data-mp="qjkaqgm3zp44">
                                {isOpen && <motion.div initial={{
                            height: 0
                          }} animate={{
                            height: 'auto'
                          }} exit={{
                            height: 0
                          }} className="overflow-hidden bg-white" data-id="element-470" data-mp="mfm83vxkrw6b">
                                    <div className="px-6 py-4 space-y-3 border-t border-charcoal/5" data-id="element-471" data-mp="3lstwbkmsd0rr">
                                      {mod.lessons.map((lesson, j) => <div key={j} className="flex items-center gap-3 text-charcoal/70" data-id="element-472" data-mp="3pt3yem7gj71c" data-mpi={j}>
                                          <PlayCircle className="w-4 h-4 text-plum/40" data-id="element-473" data-mp="20f7bde1veql5" />
                                          <span className="text-sm" data-id="element-474" data-mp="155as7m36cc0k">
                                            {lesson}
                                          </span>
                                        </div>)}
                                    </div>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>;
                    })}
                      </div>
                    </section>

                    {/* FAQs */}
                    <section data-id="element-475" data-mp="w5nr71ecx881">
                      <h3 className="font-display text-2xl font-bold text-plum mb-6" data-id="element-476" data-mp="w5xgemrkvbco">
                        Frequently Asked Questions
                      </h3>
                      <div className="space-y-4" data-id="element-477" data-mp="3ol27g1bpk6x">
                        {selectedCourse.faqs.map((faq, i) => {
                      const isOpen = openAccordion === `faq-${i}`;
                      return <div key={i} className="border-b border-charcoal/10 pb-4" data-id="element-478" data-mp="2ac29yp1fjat3" data-mpi={i}>
                              <button onClick={() => setOpenAccordion(isOpen ? null : `faq-${i}`)} className="w-full flex items-center justify-between text-left py-2" data-id="element-479" data-mp="20ffr25snyttp">
                                <span className="font-medium text-charcoal" data-id="element-480" data-mp="2y1d43no9y6y9">
                                  {faq.q}
                                </span>
                                {isOpen ? <ChevronUp className="w-5 h-5 text-fuchsia" data-id="element-481" data-mp="2o375bexrlgxm" /> : <ChevronDown className="w-5 h-5 text-charcoal/40" data-id="element-482" data-mp="18mgdnr7trdng" />}
                              </button>
                              <AnimatePresence data-id="element-483" data-mp="3fc9zv3ehcwhx">
                                {isOpen && <motion.div initial={{
                            height: 0,
                            opacity: 0
                          }} animate={{
                            height: 'auto',
                            opacity: 1
                          }} exit={{
                            height: 0,
                            opacity: 0
                          }} className="overflow-hidden" data-id="element-484" data-mp="3acnjwm79p25f">
                                    <p className="text-charcoal/70 pt-2 pb-4 text-sm leading-relaxed" data-id="element-485" data-mp="kw83l719dxnb">
                                      {faq.a}
                                    </p>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>;
                    })}
                      </div>
                    </section>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-8" data-id="element-486" data-mp="123z8b1z9zb8m">
                    {/* Instructor */}
                    <div className="bg-warmwhite p-6 rounded-2xl border border-charcoal/5" data-id="element-487" data-mp="5ft58ji04qwg">
                      <h4 className="font-display font-bold text-lg text-charcoal mb-4" data-id="element-488" data-mp="9fv12c122q3t">
                        Your Instructor
                      </h4>
                      <div className="flex items-center gap-4 mb-4" data-id="element-489" data-mp="ei2ihh4nmydj">
                        <img src={selectedCourse.instructor.avatar} alt="" className="w-16 h-16 rounded-full object-cover" data-id="element-490" data-mp="jgdrxqr1f6v8" />
                        <span className="font-bold text-plum" data-id="element-491" data-mp="1uw2c6acwprsc">
                          {selectedCourse.instructor.name}
                        </span>
                      </div>
                      <p className="text-sm text-charcoal/70 leading-relaxed" data-id="element-492" data-mp="2by9kxylex9uv">
                        {selectedCourse.instructor.bio}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Bottom CTA */}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-white border-t border-charcoal/10 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]" data-id="element-493" data-mp="2zkg1584zpvw6">
                <div data-id="element-494" data-mp="38lwardnzdtzc">
                  <p className="text-sm text-charcoal/60 mb-1" data-id="element-495" data-mp="33nl399l5rvka">
                    One-time payment
                  </p>
                  <p className="font-display text-3xl font-bold text-plum" data-id="element-496" data-mp="1e84w1zqsojx1">
                    R{selectedCourse.price}
                  </p>
                </div>
                <div className="flex w-full sm:w-auto gap-3" data-id="element-497" data-mp="19bo40ugh5leo">
                  <button className="flex-1 sm:flex-none px-6 py-3 border-2 border-plum text-plum rounded-full font-medium hover:bg-plum/5 transition-colors" data-id="element-498" data-mp="3ky87gxao85w5">
                    Preview Free
                  </button>
                  <button className="flex-1 sm:flex-none px-8 py-3 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors shadow-lg" data-id="element-499" data-mp="3c4d15lxtc9ex">
                    Enrol Now
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}